// react components
import React, {
    SVGProps,
    useEffect,
    useState,
} from 'react'
import axios from 'axios'
import {
    geoEquirectangular,
    geoPath,
} from 'd3-geo'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// data
import {
    defaultReduxState,
    media_url_site,
    media_version_site,
    reduxModalErrorEventHandlerSite,
} from 'data'

// serializers
import {
    CustomCSSProperties,
    PortfolioPageContentListSiteSerializer,
} from 'serializers/site'

// services
import {
    axiosErrorHandlerSite,
} from 'services'

// props
type MapCountry = {
    countryCode: string
    cursor: CustomCSSProperties['cursor']
    isCountryActive: boolean
    name?: string
    marker?: {
        extraInfo?: any
        lat?: number
        lng?: number
    }
    svg: SVGProps<SVGPathElement>
}

type TemplateBlock799GeoJsonSiteProps = {
    height: number
    markers?: {
        extraInfo?: any
        lat?: number
        lng?: number
    }[]
    object: PortfolioPageContentListSiteSerializer
    onMarkerClick?: (e: any) => void
    styles: CustomCSSProperties | undefined
    width: number
}

// main
export const TemplateBlock799GeoJsonSite: React.FC<TemplateBlock799GeoJsonSiteProps> = React.memo(({
    height,
    markers,
    object,
    onMarkerClick,
    styles,
    width,
}) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)

    const [mapCountries, setMapCountries] = useState<MapCountry[]>([])
    const [geoJson, setGeoJson] = useState<{
        type: string
        name: string
        crs: {
            type: string
            properties: {
                name: string
            }
        },
        features: {
            type: string
            properties: {
                ADMIN: string
                ISO_A3: string
                CONTINENT: string
                REGION_UN: string
                SUBREGION: string
                REGION_WB: string
                ISO_A2: string
            }
            geometry: {
                type: string
                coordinates: number[][][][]
            }
        }[]
    }>()
    const [hoveredCountry, setHoveredCountry] = useState<{
        name: string
        x: number
        y: number
    } | null>(null)

    const defaultColor = '#ddd'

    useEffect(() => {
        getGeoJsonData()
    }, [])

    useEffect(() => {
        constructContries()
    }, [
        geoJson,
        height,
        markers,
        object.data_json?.use_countries_2,
        styles,
        width,
    ])

    function getGeoJsonData() {
        try {
            const apiUrl = `${media_url_site}global/data/geojson-world.json${media_version_site}`
            axios({
                method: 'get',
                url: apiUrl,
            })
                .then((response) => {
                    setGeoJson(response.data)
                })
                .catch((error) => {
                    axiosErrorHandlerSite({
                        apiUrl: apiUrl,
                        component: 'TemplateBlock799GeoJsonSite',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'getGeoJsonData',
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock799GeoJsonSite',
                'getGeoJsonData',
            ))
        }
    }

    function constructContries() {
        try {
            if (!markers || !geoJson) return

            const allowedContinents = object.data_json?.mapGeojsonContinent
            const filteredFeatures = geoJson.features.filter((feature) => {
                if (Array.isArray(allowedContinents) && allowedContinents.length > 0) {
                    return allowedContinents.includes(feature.properties.CONTINENT)
                }
                return true
            })

            // Dynamically fit projection only to filtered features
            const projection = geoEquirectangular().fitSize([width, height], {
                ...geoJson,
                features: filteredFeatures,
            } as any)

            const geoPathGenerator = geoPath().projection(projection)

            const activeCountriesDict: { [key: string]: any } =
                markers.reduce((a, c) => {
                    return {
                        ...a,
                        [c.extraInfo.address_json[0].countryCode]: c,
                    }
                }, {})

            const countries = filteredFeatures.map((feature) => {
                let svgProps: SVGProps<SVGPathElement> = {
                    d: geoPathGenerator(feature as any) || '',
                    stroke: defaultColor,
                    fill: defaultColor,
                }

                let isCountryActive = false

                if (activeCountriesDict[feature.properties.ISO_A2!]) {
                    isCountryActive = true
                    svgProps = {
                        ...svgProps,
                        stroke: styles?.svgStrokeColor || defaultColor,
                        fill: styles?.svgFillColor || defaultColor,
                    }
                }

                return {
                    marker: activeCountriesDict[feature.properties.ISO_A2!],
                    countryCode: feature.properties.ISO_A2,
                    cursor: 'initial',
                    isCountryActive,
                    svg: svgProps,
                    name: feature.properties.ADMIN,
                } as MapCountry
            })

            setMapCountries(countries || [])
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock799GeoJsonSite',
                'constructContries',
            ))
        }
    }

    function handleMouseLeaveCountry(country: MapCountry) {
        try {
            setHoveredCountry(null)
            setMapCountries(mapCountries.map(m => {
                if (isMatchCountry(m, country)) {
                    return {
                        ...m,
                        cursor: 'initial',
                        svg: {
                            ...m.svg,
                            stroke: styles?.svgStrokeColor || defaultColor,
                            fill: styles?.svgFillColor || defaultColor,
                        }
                    }
                }
                return m
            }))
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(error, 'TemplateBlock799GeoJsonSite', 'handleMouseOverCountry'))
        }
    }

    function handleMouseOverCountry(event: React.MouseEvent, country: MapCountry) {
        try {
            setMapCountries(mapCountries.map(m => {
                if (isMatchCountry(m, country)) {
                    return {
                        ...m,
                        cursor: 'pointer',
                        svg: {
                            ...m.svg,
                            stroke: styles?.svgHoverStrokeColor || defaultColor,
                            fill: styles?.svgHoverFillColor || defaultColor,
                        }
                    }
                }
                return m
            }))
            setHoveredCountry({
                name: country.name || country.countryCode,
                x: event.clientX,
                y: event.clientY,
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(error, 'TemplateBlock799GeoJsonSite', 'handleMouseOverCountry'))
        }
    }

    function isMatchCountry(source: MapCountry, target: MapCountry) {
        try {
            if (object.data_json?.use_countries_2) {
                return source.isCountryActive
                    && target.isCountryActive
                    && source.marker?.extraInfo?.id === target.marker?.extraInfo?.id
            }
            return source.isCountryActive
                && target.isCountryActive
                && source.countryCode === target.countryCode
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock799GeoJsonSite',
                'handleMouseOverCountry',
            ))
        }
    }

    return (
        <>
            <svg
                className='t-799-geojson-map'
                height={height}
                style={{
                    background: styles?.svgBackground,
                }}
                width={width}
            >
                {mapCountries.map(country => (
                    <path
                        key={country.countryCode}
                        id={country.countryCode}
                        {...country.svg as any}
                        onClick={onMarkerClick ? () => onMarkerClick(country.marker) : undefined}
                        onMouseLeave={() => handleMouseLeaveCountry(country)}
                        onMouseOver={(e) => handleMouseOverCountry(e, country)}
                        style={{ cursor: country.cursor }}
                    />
                ))}
            </svg>
            {hoveredCountry && (
                <div
                    style={{
                        background: 'black',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '12px',
                        left: hoveredCountry.x + 10,
                        padding: '4px 8px',
                        pointerEvents: 'none',
                        position: 'fixed',
                        top: hoveredCountry.y + 10,
                        zIndex: 1000,
                    }}
                >
                    {hoveredCountry.name}
                </div>
            )}
        </>
    )
})
